// Copyright (c) 2024-2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useContext } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { ThemeProvider } from './context/ThemeContext';

// Importing custom pages
import Login from './pages/Login/Login';
import LoadingScreen from './components/LoadingScreen';
import FeedTimestamping from './pages/FeedTimestamping/FeedTimestamping';
import Corrections from './pages/Corrections/Corrections';
import RotationPrompt from './components/common/RotationPrompt';
import AccessDenied from './pages/AccessDenied/AccessDenied';

// Importing custom hooks
import useOrientation from './hooks/useOrientation';

// Importing context provider.
import { auth } from './context/FirebaseConfig';
import { AuthContext } from './context/AuthContext';

function App() {
  // Check if device is in portrait mode with enhanced options
  const { isPortrait } = useOrientation({
    mobileMaxWidth: 480,
    tabletMaxWidth: 1024,
    onlyMobileTablet: true,
    useNativeAPI: true,
  });

  // finding the current user
  const { authUser, currentUser, userLoaded } = useContext(AuthContext);

  // Creating a protected route to only allow logged-in users to access the dashboard.
  const ProtectedRoute = ({ children }) => {
    if (!authUser) {
      return <Navigate to='/login' />;
    }
    // else
    return children;
  };

  const LimitedAccessRoute = ({ acceptedUserFlags, children }) => {
    if (
      currentUser &&
      currentUser.info &&
      currentUser.info.user_flags &&
      acceptedUserFlags.some((flag) => currentUser.info.user_flags.includes(flag))
    ) {
      return children;
    }

    signOut(auth);
    return <Navigate to='/login?error=user-flags' />;
  };

  // Enhanced route that checks stamp permissions and production lines
  const StampAccessRoute = ({ children }) => {
    // Show loading while user data is being fetched
    if (!userLoaded) {
      return <LoadingScreen />;
    }

    // Check if stamp is enabled for this client (deny access if false or null for security)
    if (currentUser && currentUser.stampEnabled !== true) {
      return <Navigate to='/stamp-access-denied' replace />;
    }

    // Check if user has production lines (devices) configured
    if (currentUser && currentUser.hasProductionLines === false) {
      return <Navigate to='/no-production-lines' replace />;
    }

    return children;
  };

  // Simple protection for error pages - just require authentication
  const ErrorPageRoute = ({ children }) => {
    if (!userLoaded) {
      return <LoadingScreen />;
    }

    // Just ensure user is authenticated to see error pages
    // The actual permission validation happens in the main app flow
    return children;
  };

  if (authUser && !userLoaded) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider>
      <BrowserRouter>
        {/* If the device is in portrait mode and the user is authenticated, show the rotation prompt */}
        {/* We don't show it on the login page to allow users to log in regardless of orientation */}
        {isPortrait && authUser ? (
          <RotationPrompt />
        ) : (
          <Routes>
            <Route path='/'>
              <Route index element={<Navigate to={'feed-timestamping'} />} />
              <Route path='login' element={<Login />} />
              <Route
                path='stamp-access-denied'
                element={
                  <ProtectedRoute>
                    <ErrorPageRoute>
                      <AccessDenied type='STAMP_DISABLED' />
                    </ErrorPageRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path='no-production-lines'
                element={
                  <ProtectedRoute>
                    <ErrorPageRoute>
                      <AccessDenied type='NO_PRODUCTION_LINES' />
                    </ErrorPageRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path='feed-timestamping'
                element={
                  <ProtectedRoute>
                    <StampAccessRoute>
                      <LimitedAccessRoute acceptedUserFlags={['admin', 'manager', 'stamper']}>
                        <FeedTimestamping />
                      </LimitedAccessRoute>
                    </StampAccessRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path='corrections'
                element={
                  <ProtectedRoute>
                    <StampAccessRoute>
                      <LimitedAccessRoute acceptedUserFlags={['admin', 'manager', 'stamper']}>
                        <Corrections />
                      </LimitedAccessRoute>
                    </StampAccessRoute>
                  </ProtectedRoute>
                }
              />
              <Route path='*' element={<Navigate to='/login' replace />} />
            </Route>
          </Routes>
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
